import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, EmptyState } from "@/components/common";
import { LanguageSelect } from "@/components/LanguageSelect";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lightbulb, Lock, PenLine, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { IdeaCheckout } from "@/components/IdeaCheckout";

const PLAN_RANK = { gratis: 0, start: 1, pro: 2, business: 2 };

const CATEGORIES = [
  { value: "linkedin", label: "LinkedIn-opslag", route: "/linkedin" },
  { value: "facebook", label: "Facebook-opslag", route: "/facebook" },
  { value: "debate", label: "Debatindlæg", route: "/debatindlaeg", min: "start", ideasOpen: true, upsell: "Opgradér for at få Aicom til at skrive et færdigt debatindlæg ud fra idéen." },
  { value: "newsletter", label: "Nyhedsbrev", route: "/nyhedsbrev", min: "start", ideasOpen: true, upsell: "Opgradér for at få Aicom til at skrive et færdigt nyhedsbrev ud fra idéen." },
  { value: "press", label: "Presse", route: "/presse", min: "start", ideasOpen: true, upsell: "Opgradér for at få Aicom til at vurdere historiens pressepotentiale og skrive en færdig pressemeddelelse ud fra idéen." },
  { value: "campaign", label: "Kampagne", route: "/kampagner", min: "pro", ideasOpen: true, upsell: "Opgradér for at få Aicom til at skrive færdige kampagnetekster til annoncer og landingssider ud fra idéen." },
  { value: "brand", label: "Brandfortælling", route: "/brandfortaelling", min: "pro", ideasOpen: true, upsell: "Opgradér for at få Aicom til at skrive jeres færdige brandfortælling med pitch, kernebudskab og taglines ud fra idéen." },
  { value: "lead", label: "Leadgenerator", route: "/leadgenerator", min: "pro", ideasOpen: true, upsell: "Opgradér for at få Aicom til at bygge en færdig lead magnet med kampagne ud fra idéen." },
];

const hasPlan = (user, min) => user?.role === "admin" || !min || PLAN_RANK[user?.plan || "gratis"] >= PLAN_RANK[min];

export default function Ideas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, contentLang } = useLocale();
  const categories = CATEGORIES.filter(
    (c) => c.ideasOpen || hasPlan(user, c.min)
  );
  const [locked, setLocked] = useState(null);
  const [teaser, setTeaser] = useState(null);
  const [paying, setPaying] = useState(false);
  const [params] = useSearchParams();
  const [category, setCategory] = useState(() => (categories.some((c) => c.value === params.get("kanal")) ? params.get("kanal") : "linkedin"));
  const [ideas, setIdeas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState(contentLang);

  const generate = async () => {
    setLoading(true);
    setIdeas(null);
    try {
      const { data } = await api.post("/ideas", { category, language });
      setIdeas(data.ideas);
    } catch (err) {
      toast.error(apiError(err));
      setIdeas([]);
    } finally {
      setLoading(false);
    }
  };

  const cat = CATEGORIES.find((c) => c.value === category);
  const draft = (idea) => {
    if (!hasPlan(user, cat?.min)) {
      setLocked(idea);
      setPaying(false);
      setTeaser(null);
      api.post("/ideas/teaser", { category, title: idea.title, description: idea.description, language })
        .then(({ data }) => setTeaser(data))
        .catch(() => setTeaser({ headline: "", text: "" }));
      return;
    }
    if (category === "lead") {
      navigate("/leadgenerator", { state: { leadIdea: idea } });
      return;
    }
    const route = cat?.route || "/linkedin";
    navigate(route, { state: { topic: `${idea.title}. ${idea.description}` } });
  };

  return (
    <div>
      <PageHeader overline="Idégenerator" title="Idéer, når I mangler noget at sige"
        subtitle="Vælg en kanal, og få 10 skræddersyede idéer baseret på jeres profil." />

      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="sm:w-64" data-testid="ideas-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{t(c.label)}</SelectItem>)}</SelectContent>
        </Select>
        <LanguageSelect value={language} onChange={setLanguage} disabled={loading} testid="ideas-language" />
        <Button onClick={generate} disabled={loading} className="gap-2" data-testid="ideas-generate-btn">
          <Sparkles className="h-4 w-4" /> {t("Generér 10 idéer")}
        </Button>
      </div>

      {loading && <Spinner label="Finder idéer til jer..." />}
      {ideas !== null && ideas.length === 0 && !loading && (
        <EmptyState icon={Lightbulb} title="Ingen idéer endnu" subtitle="Vælg en kanal og generér." testid="ideas-empty" />
      )}
      {ideas && ideas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ideas-list">
          {ideas.map((idea, i) => (
            <Surface key={i} delay={i * 0.04} testid={`idea-${i}`} className="flex flex-col">
              <div className="flex items-start gap-3">
                <span className="font-mono text-xs text-muted-foreground mt-1">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <h3 className="font-display font-medium leading-snug">{idea.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{idea.description}</p>
                </div>
              </div>
              <Button size="sm" variant="secondary" onClick={() => draft(idea)} className="mt-4 gap-2 self-start" data-testid={`idea-draft-${i}`}>
                <PenLine className="h-3.5 w-3.5" /> {t("Skriv udkast")}
              </Button>
            </Surface>
          ))}
        </div>
      )}

      <Dialog open={!!locked} onOpenChange={(o) => !o && setLocked(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="idea-upgrade-dialog">
          <DialogHeader>
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
              <Lock className="h-3.5 w-3.5" /> {t("Kræver {plan}-planen", { plan: cat?.min === "pro" ? "Pro" : "Start" })}
            </div>
            <DialogTitle className="font-display leading-snug" data-testid="idea-upgrade-title">{locked?.title}</DialogTitle>
            <DialogDescription className="leading-relaxed pt-1">{locked?.description}</DialogDescription>
          </DialogHeader>
          {paying ? (
            <IdeaCheckout idea={locked} plan={cat?.min} route={cat?.route} onBack={() => setPaying(false)} />
          ) : (
            <>
              <TeaserPreview teaser={teaser} t={t} />
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="idea-upgrade-upsell">
                {t(cat?.upsell || "")}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button variant="secondary" onClick={() => setLocked(null)} data-testid="idea-upgrade-close">{t("Luk")}</Button>
                <Button onClick={() => setPaying(true)} className="gap-2" data-testid="idea-upgrade-btn">
                  <Sparkles className="h-4 w-4" /> {t("Opgradér og skriv")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeaserPreview({ teaser, t }) {
  if (!teaser) {
    return (
      <div className="rounded-lg border bg-white p-4 space-y-2" data-testid="idea-teaser-loading">
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{t("Aicom skriver en smagsprøve... (vent venligst)")}</p>
        {[90, 100, 80].map((w) => <div key={w} className="h-3 rounded bg-muted animate-pulse" style={{ width: `${w}%` }} />)}
      </div>
    );
  }
  if (!teaser.text) return null;
  const [first, ...rest] = teaser.text.split(/(?<=[.!?])\s+/);
  return (
    <div className="relative rounded-lg border bg-white p-4 overflow-hidden" data-testid="idea-teaser">
      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">{t("Smagsprøve")}</p>
      {teaser.headline && <h4 className="font-display font-medium leading-snug mb-2" data-testid="idea-teaser-headline">{teaser.headline}</h4>}
      <p className="text-sm leading-relaxed">
        {first}{" "}
        <span className="blur-[4px] select-none" aria-hidden="true" data-testid="idea-teaser-blurred">{rest.join(" ")}</span>
      </p>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent" />
    </div>
  );
}
